import { Component, Input } from '@angular/core';
import { TourInformation } from 'src/app/interfaces/tour-information';
import { FilterService } from 'src/app/services/filter.service';
import { Search } from 'src/app/interfaces/search';
import { UntypedFormControl, UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import { FilterItemsDisplayComponent } from "../filter-items-display/filter-items-display.component";
import { IonicModule } from '@ionic/angular';
import { LoadscreenComponent } from '../loadscreen/loadscreen.component';
import { ToastService } from 'src/app/services/toast.service';

@Component({
  selector: 'app-filter-search',
  templateUrl: './filter-search.component.html',
  styleUrls: ['./filter-search.component.scss'],
  standalone: true,
  imports: [
    IonicModule,
    ReactiveFormsModule,
    FilterItemsDisplayComponent,
    LoadscreenComponent
  ]
})
export class FilterSearchComponent {

  // Attribute inputs for the custom element (empty JSON-string is default value)
  @Input() public settings: string = '{}'
  @Input() public translations: string = '{}'
  @Input() public filterData: string = '{}'
  @Input() public debug: boolean = false

  // Filter data
  public filterOptions: any = {}
  public filterResults: TourInformation[] = []
  public filteredResults: TourInformation[] = []

  public isLoading: boolean = false
  public path: string = window.location.pathname

  public currency: string = 'EUR'

  public defaultDateFrom: Date = new Date()
  public defaultDateTo: Date = new Date()
  public minSelectedDate: Date = new Date()
  public maxSelectedDate: Date
  public datetimeButtonFormat: object = {
    date: {
      weekday: 'short',
      month: 'long',
      day: '2-digit',
      year: 'numeric'
    }
  }

  // Filter formgroup
  public filterForm = new UntypedFormGroup({
    text: new UntypedFormControl(''),
    priceFrom: new UntypedFormControl(''),
    priceTo: new UntypedFormControl(''),
    dateFrom: new UntypedFormControl(''),
    dateTo: new UntypedFormControl(''),
    location: new UntypedFormControl(''),
    type: new UntypedFormControl(''),
    qty: new UntypedFormControl(''),
    difficulty: new UntypedFormControl(''),
    owners: new UntypedFormControl({ value: '', disabled: false }),
    ticketTypes: new UntypedFormControl(''),
  })


  constructor(
    private searchService: FilterService,
    private toastService: ToastService
  ) {
    this.isLoading = true
  }


  ngOnInit() {

    // Save inputs in services for later use
    this.filterResults = JSON.parse(this.filterData) as TourInformation[]

    // Set settings we need now
    this.currency = 'DKK'

    // Get URL params for pre-populating the filter form
    let urlParams: any = {}
    new URL(window.location.href).searchParams.forEach((value, key) => {
      urlParams[key] = this.searchService.formatParamFilterOption(key, value)
    })

    // Patch form and filter results
    if (urlParams) {
      this.filterForm.patchValue(urlParams)
    }

    // Get filter data from the service
    this.getFilterData()

    // Live filtering
    // Subscribe to filterForm value changes and filter results on changes
    this.filterForm.valueChanges.subscribe(() => {
      this.filteredResults = this.searchService.filterDataByFields(this.filterResults, this.filterForm.value as Search)

      // Update selected filter options with reduced options based on the results (If no owner is selected)
      if (!this.filterForm.controls['owners'].value || !this.filterForm.controls['owners'].value.length) {
        this.filterOptions.owners = this.searchService.buildFilterOptionsFromTours(this.filteredResults, ['owners']).owners
      }

      // Create a clone of the filterForm that can be manipulated
      let structuredFilterForm: any = structuredClone(this.filterForm.value)

      for (let [key, value] of Object.entries<any>(structuredFilterForm)) {

        // Delete all empty entries so they don't appear in the URL
        if (!value || (value instanceof Array && value.length === 0)) {
          delete structuredFilterForm[key]
          continue
        }

        if (key === 'dateFrom' || key === 'dateTo') {
          let date = new Date(value)
          structuredFilterForm[key] = date.getFullYear() + '-' + ('0' + (date.getMonth() + 1)).slice(-2) + '-' + ('0' + date.getDate()).slice(-2);
        }

        // Stringify arrays, so they dont' get added doubly to the URL
        if (value instanceof Array && value.length > 0) {
          structuredFilterForm[key] = value.toString()
        }
      }

      // Add form values to the current window URL
      const queryParams = new URLSearchParams(structuredFilterForm).toString()

      window.history.replaceState(null, '', this.path + '?' + queryParams)
    })
  }


  /**
   * Fetches filter data from the search service and initializes the filter form
   * Sets filterResults, filteredResults and filterOptions based on the response
   * Shows error toast if the request fails
   */
  public getFilterData() {
    this.isLoading = true
    this.searchService.getFilterData().subscribe({
      next: (data: any) => {
        
        this.filterResults = data.filterData
        this.filteredResults = this.searchService.filterDataByFields(this.filterResults, this.filterForm.value as Search)
        this.filterOptions = data.filterOptions
        this.initFilterForm()
        this.isLoading = false
      },
      error: (error: any) => {
        this.toastService.presentToast('Error loading filter data! - ' + error.message, 'danger', 10000)
        this.isLoading = false
      }
    })
  }


  /**
   * Updates the price range in the filter form by patching the priceFrom and priceTo values
   * @param $event The event object from the price range change
   */
  public updatePrice($event) {
    this.filterForm.patchValue({
      'priceFrom': this.filterForm.controls['priceFrom'].value,
      'priceTo': this.filterForm.controls['priceTo'].value
    })
  }


  /**
   * Sets the date range in the filter form based on user selection
   * Updates minSelectedDate and maxSelectedDate to maintain valid date ranges
   * Formats the selected date to YYYY-MM-DD format before updating form
   * @param field The form field to update ('dateFrom' or 'dateTo')
   * @param $event The event containing the selected date value
   */
  public setDateRange(field: string, $event: CustomEvent) {

    if(field === 'dateFrom') {
      this.minSelectedDate = new Date($event.detail.value)
    }
    if(field === 'dateTo') {
      if(new Date($event.detail.value) < this.minSelectedDate) {
        this.minSelectedDate = new Date($event.detail.value)
        this.maxSelectedDate = new Date($event.detail.value)
      }
    }

    let date = new Date($event.detail.value);
    let formattedDate = date.getFullYear() + '-' + ('0' + (date.getMonth() + 1)).slice(-2) + '-' + ('0' + date.getDate()).slice(-2);
    this.filterForm.patchValue({
      [field]: formattedDate
    });
  }


  /**
   * Reset the filter form
   **/
  public resetFilters() {
    this.filterForm.reset()
    // Re-initialize the filter form with default values
    this.initFilterForm()
    this.getFilterData()
  }


  /**
   * Initialize some default values for the filter form
   */
  private initFilterForm() {
    // Set default price range to set the slider
    if (this.filterOptions.price && this.filterOptions.price.length > 0) {
      this.filterForm.patchValue({
        'priceFrom': this.filterOptions.price[0],
        'priceTo': this.filterOptions.price[1]
      })
    }
  }
}
